/**
 *
 * AppVerifyStepTwo Component
 *
 */

import {
  Button,
  Enums,
  stylize,
  TextField,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect } from "react";
import withIntl from "utils/withIntl";
import { AppTypeEnum, StatusTypeEnum } from "./enums";
import messages from "./messages";
import styles, { Styles } from "./styles";

const { ButtonStyle, ButtonSize } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  onCancel: Function;
  onBack: Function;
  onSubmitPassCode: Function;
  resetSubmissionStatus: Function;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */

  processingStatus: string;
}

export interface AppVerifyStepTwoProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {
  processingStatus: StatusTypeEnum.INITIAL,
};

type Props = InjectedProps & AppVerifyStepTwoProps;

const AppVerifyStepTwo = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    onCancel,
    onBack,
    onSubmitPassCode,
    processingStatus,
    resetSubmissionStatus,
  } = props;

  const [code, setCode] = React.useState("");
  const [passcodeError, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (processingStatus === StatusTypeEnum.ERROR) {
      setError(formatMessage(messages.invalidCodeErrorMessage));
    } else {
      setError(null);
    }
  }, [processingStatus]);

  useEffect(
    () => () => {
      resetSubmissionStatus();
    },
    []
  );

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div data-testid="app-verify-step-two-code-input">
        <TextField
          label={formatMessage(messages.codeTextField)}
          text={code}
          textStyle={{fontFamily: "Red Hat Text"}}
          onChangeText={(text) => setCode(text)}
          className={classnames(
            classes.textfield,
            "header-font-family",
            "no-border-radius"
          )}
          errorMessage={passcodeError}
          aria-label="passcode textfield"
        />
      </div>
      <div>
        <Button
          buttonStyle={ButtonStyle.Link}
          buttonSize={ButtonSize.Regular}
          className={classnames(
            classes.linkButton,
            "no-border-radius",
            "header-text-font-family"
          )}
          name={formatMessage(messages.backButtonLabel)}
          onClick={() => onBack()}
          aria-label="back button"
        />
      </div>
      <div>
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.Regular}
          className={classnames(classes.verifyButton, "no-border-radius", "header-text-font-family")}
          name={formatMessage(messages.verifyButtonLabel)}
          onClick={() => {
            onSubmitPassCode({
              authenticatorType: AppTypeEnum.APP,
              passcode: code,
            });
          }}
          disabled={!code}
          loading={processingStatus === StatusTypeEnum.PROCESSING}
          aria-label="verify button"
        />
      </div>
      <div>
        <Button
          buttonStyle={ButtonStyle.Link}
          buttonSize={ButtonSize.Regular}
          className={classnames(classes.linkButton, "header-text-font-family")}
          name={formatMessage(messages.cancelButtonLabel)}
          onClick={() => onCancel()}
          aria-label="cancel button"
        />
      </div>
    </div>
  );
};

AppVerifyStepTwo.defaultProps = defaultProps;

export default withIntl(stylize<Props, DefaultProps>(styles)(AppVerifyStepTwo));
